import React, { useCallback, useEffect, useState } from 'react';

import { find, get, isEmpty } from 'lodash';

import { TopBarSelect, useNavbar } from 'pages/Teachers/shared';
import { default as pusherManager } from 'utils/integrations/pusher';

import {
  buildSectionsOptions,
  ensureStudentWorkSelect,
  getVisibleStudentWorkList,
  setVisibleStudentWorkSelected,
  toggleAllStudentWorkSelectValue,
  toggleStudentWorkItemSelect
} from './utils';

export const useNavigation = ({
  assignment,
  selectedSectionId,
  sections,
  selectSection
}) => {
  const { navigation } = useNavbar({ title: '' });

  useEffect(() => {
    if (assignment) {
      navigation.setTitle(get(assignment, 'tracker_name'));
    } else {
      navigation.setTitle('');
    }
  }, [navigation, assignment]);

  const renderValue = useCallback(
    (value) => {
      return get(find(sections, { id: value }), 'section_name', '');
    },
    [sections]
  );

  useEffect(() => {
    if (!isEmpty(sections) && selectedSectionId) {
      navigation.setLeftSubheader(
        <TopBarSelect
          value={selectedSectionId}
          renderValue={renderValue}
          onChange={selectSection}
          options={buildSectionsOptions(sections)}
        />
      );
    }
  }, [navigation, selectedSectionId, sections, selectSection, renderValue]);
};

export const usePusherManager = (selectedSectionId, webhookCallback) => {
  useEffect(() => {
    if (selectedSectionId) {
      pusherManager.stopListeningForNewStudentWork(selectedSectionId);
      pusherManager.listenForNewStudentWork(selectedSectionId, webhookCallback);
    }
  }, [selectedSectionId, webhookCallback]);

  useEffect(() => {
    return () => pusherManager.disconnect();
  }, []);
};

export const usePulseEffect = () => {
  const [active, setActive] = useState(false);

  const start = useCallback(() => setActive(true), []);
  const stop = useCallback(() => setActive(false), []);

  return [active, start, stop];
};

const useTooltip = () => {
  const [opened, setOpened] = useState(false);

  const toggle = useCallback(() => setOpened((prev) => !prev), []);
  const close = useCallback(() => setOpened(false), []);

  return [opened, toggle, close];
};

export const usePageTooltips = () => {
  const [multiSelectTooltipOpen, toggleMultiSelectTooltip] = useTooltip();
  const [tutoringTooltipOpen, closeTutoringTooltip] = useTooltip();
  const onMultiSelectTooltipClick = useCallback(() => {
    toggleMultiSelectTooltip();
    closeTutoringTooltip();
  }, [toggleMultiSelectTooltip, closeTutoringTooltip]);

  return [
    multiSelectTooltipOpen,
    onMultiSelectTooltipClick,
    tutoringTooltipOpen
  ];
};

export const useAccordion = () => {
  const [opened, setOpened] = useState(false);
  const toggle = useCallback(() => setOpened((prev) => !prev), []);

  return [opened, toggle];
};

const defaultFilters = { tasks: [], show: { value: '' }, students: [] };
export const useFilters = () => {
  const [filters, setFilters] = useState(defaultFilters);
  return [filters, setFilters];
};

export const useOrdering = () => {
  const [ordering, setOrdering] = useState({ value: '' });
  return [ordering, setOrdering];
};

export const useVisibleStudentWork = (
  studentWork,
  filters,
  ordering,
  allTooltipsClosed,
  activeTasksIds
) => {
  const [studentWorkList, setStudentWorkList] = useState([]);

  useEffect(() => {
    if (!isEmpty(studentWork)) {
      const studentWorkList = getVisibleStudentWorkList({
        studentWork,
        filters,
        ordering
      });

      setStudentWorkList((prev) =>
        ensureStudentWorkSelect({
          studentWorkList: prev,
          newStudentWorkList: studentWorkList
        })
      );
    }
  }, [studentWork, filters, ordering]);

  const toggleSelectStudentWorkItem = useCallback(
    ({ studentId, trackerScoreResponseId, selected }) => {
      setStudentWorkList((swList) =>
        toggleStudentWorkItemSelect({
          studentWorkList: swList,
          selected,
          studentId,
          trackerScoreResponseId
        })
      );
    },
    []
  );

  const toggleSelectAllVisibleStudentWork = useCallback(
    ({ includeEmpty = true }) =>
      setStudentWorkList((swList) =>
        setVisibleStudentWorkSelected(
          swList,
          activeTasksIds,
          filters.show.value,
          includeEmpty
        )
      ),
    [filters.show, activeTasksIds]
  );

  useEffect(() => {
    if (allTooltipsClosed) {
      setStudentWorkList((swList) =>
        toggleAllStudentWorkSelectValue({
          studentWorkList: swList,
          selected: false
        })
      );
    }
  }, [allTooltipsClosed]);

  return [
    studentWorkList,
    toggleSelectStudentWorkItem,
    toggleSelectAllVisibleStudentWork
  ];
};

export const useActiveTasksIds = () => {
  const [activeTasksIds, storeActiveTasksIds] = useState([]);

  const storeActiveTaskId = useCallback((id) => {
    storeActiveTasksIds((prev) => [...prev, id]);
  }, []);
  const removeActiveTaskId = useCallback((id) => {
    storeActiveTasksIds((prev) => prev.filter((x) => x !== id));
  }, []);

  return [activeTasksIds, storeActiveTaskId, removeActiveTaskId];
};
